<template>
  <div>
    <RouterLink
      :to="{ name: 'ComplianceGroup', params: { group: 'eecs' } }"
      class="btn btn-sm btn-outline-secondary mb-3"
      >← Consolidated view</RouterLink
    >
    <h1 class="cg-page-title mb-4">Sourcing request submitted</h1>

    <SectionCard title="Request progress" note="Recorded 10 September 2026">
      <div class="table-responsive">
        <table class="table table-sm mb-0 align-middle small">
          <thead>
            <tr>
              <th class="cg-micro">Line</th>
              <th class="cg-micro text-end">Volume</th>
              <th class="cg-micro">Required production month</th>
              <th class="cg-micro">State</th>
              <th class="cg-micro">Next evidence expected</th>
            </tr>
          </thead>
          <tbody class="cg-tabular">
            <tr v-if="short">
              <td class="fw-semibold">France · {{ short.period }}</td>
              <td class="text-end">{{ fmt(short.gap) }}</td>
              <td>{{ short.period }}</td>
              <td>
                <ToneBadge
                  tone="pending"
                  label="Submitted · Soldera sourcing"
                />
              </td>
              <td class="text-body-secondary">Firm quote</td>
            </tr>
          </tbody>
        </table>
      </div>
    </SectionCard>

    <SectionCard
      eyebrow="Historical completed evidence"
      :title="HISTORICAL_EVIDENCE.title"
      note="Issued by EEX. Original document, not a Soldera-generated summary."
    >
      <div class="cg-evidence">
        <table class="table table-sm mb-0 cg-key-table">
          <tbody>
            <tr>
              <td class="text-body-secondary">Evidence type · issuer</td>
              <td>{{ HISTORICAL_EVIDENCE.issuer }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Beneficiary</td>
              <td>{{ HISTORICAL_EVIDENCE.beneficiary }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Claim purpose</td>
              <td>{{ HISTORICAL_EVIDENCE.purpose }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Consumption period · volume</td>
              <td class="cg-tabular">{{ HISTORICAL_EVIDENCE.periodVolume }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Linked site</td>
              <td>{{ HISTORICAL_EVIDENCE.site }}</td>
            </tr>
          </tbody>
        </table>
        <table class="table table-sm mb-0 cg-key-table">
          <tbody>
            <tr>
              <td class="text-body-secondary">Certificate lot</td>
              <td class="cg-mono">{{ HISTORICAL_EVIDENCE.lot }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Registry transaction</td>
              <td class="cg-mono">{{ HISTORICAL_EVIDENCE.txn }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Authorized</td>
              <td>{{ HISTORICAL_EVIDENCE.authorized }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Received date</td>
              <td>{{ HISTORICAL_EVIDENCE.received }}</td>
            </tr>
            <tr>
              <td class="text-body-secondary">Verification</td>
              <td>{{ HISTORICAL_EVIDENCE.verification }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="d-flex gap-2 mt-3 flex-wrap align-items-center">
        <button type="button" class="btn btn-sm btn-outline-secondary">
          Original PDF
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="lineageOpen = true"
        >
          Lot lineage
        </button>
        <ToneBadge tone="ok" label="Completed · no open exception" />
      </div>
    </SectionCard>

    <LotLineageModal
      v-if="lineageOpen"
      :rows="HISTORICAL_EVIDENCE.lineage"
      :lot="HISTORICAL_EVIDENCE.lot"
      @close="lineageOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import LotLineageModal from "./components/LotLineageModal.vue";
import SectionCard from "./components/SectionCard.vue";
import ToneBadge from "./components/ToneBadge.vue";
import {
  HISTORICAL_EVIDENCE,
  isGroupKey,
} from "./fixture/complianceGroupsFixture";
import { franceShortMonth } from "./fixture/derive";
import { fmt } from "./fixture/format";
import { useComplianceDemoStore } from "./useComplianceDemoStore";

const route = useRoute();
const router = useRouter();
const store = useComplianceDemoStore();
// Only reachable for the EECS group and only once a request exists in this session; a reload
// resets the in-memory store, so direct navigation goes back to the request form.
watch(
  () => [route.params.group, store.state.requestSubmitted] as const,
  ([group, submitted]) => {
    if (group !== "eecs") {
      router.replace({
        name: "ComplianceGroup",
        params: { group: isGroupKey(group) ? group : "eecs" },
      });
    } else if (!submitted) {
      router.replace({
        name: "ComplianceGroupSourceRequest",
        params: { group: "eecs" },
      });
    }
  },
  { immediate: true }
);
const short = computed(() => franceShortMonth(store.domains.fr));
const lineageOpen = ref(false);
</script>

<style scoped lang="scss">
.cg-evidence {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}
</style>
